/**
 * Rich-text content block. Uses :func:`StreamingMarkdown` so partial
 * input renders cleanly while Claude is still streaming.
 *
 * Vertical rhythm lives one level up in :class:`AssistantStream` —
 * the parent flex column gives every block the same uniform gap.
 * The arbitrary-variant first/last selectors here strip the outer
 * margins of the contained markdown so the visible gap between two
 * text blocks (or between a text block and an adjacent tool row)
 * matches the parent's gap exactly — no compounding from the
 * ``<p>``/``<ul>`` etc.'s natural top/bottom margins. Inner
 * paragraph rhythm (``my-1`` between consecutive paragraphs in the
 * same block) is preserved because those siblings are neither
 * ``:first-child`` nor ``:last-child``.
 *
 * During live streaming, the empty ``content_block_start`` frame is
 * intentionally silent so it does not stack a blank text placeholder.
 * Any real ``text_delta`` renders immediately.
 */

import * as React from 'react';
import type { TextBlock } from '@/lib/agent/types';
import { StreamingMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils/cn';
import { useProjectCwd } from './ProjectCwdContext';
import { useSteadyRevealText } from './useSteadyRevealText';

export interface TextBlockViewProps {
  block: TextBlock;
  /** True while source deltas are still arriving. */
  streaming?: boolean;
  className?: string;
}

export const TextBlockView = React.memo(function TextBlockView({
  block,
  streaming = false,
  className,
}: TextBlockViewProps) {
  const text = block.text ?? '';
  const cwd = useProjectCwd();
  const visibleText = useSteadyRevealText(text, streaming);
  if (!text) return null;
  return (
    <div className={cn('relative select-text', streaming && 'min-h-[1lh]', className)}>
      <StreamingMarkdown
        closeUnterminated={streaming}
        streaming={streaming}
        codePaneCwd={cwd}
        className="[&>:first-child]:mt-0 [&>:last-child]:mb-0"
      >
        {visibleText}
      </StreamingMarkdown>
    </div>
  );
});
